import { useState } from "react"
import { Platform, ScrollView } from "react-native"
import {
  ButtonV2,
  CloseKeyboardContainer,
  KeyboardAvoidingView,
  Spacing,
  Text,
} from "../../ui-components"
import { OnboardingScreenWrapper } from "./OnboardingScreenWrapper.tsx"
import { PhoneNumberInput } from "../PhoneNumberInput/PhoneNumberInput.tsx"
import styled from "@emotion/native"

type PhoneNumberScreenUIProps = {
  switchToLogInScreen?: () => void
  proceed: (params: { countryCode: string; phoneNumber: string }) => Promise<void>
  isSendingText: boolean
  showLogInButtonOnWeb?: boolean
  hideLogInLink?: boolean
  onBack?: () => void
}

export function PhoneNumberScreenUI({
  switchToLogInScreen,
  proceed,
  isSendingText,
  showLogInButtonOnWeb,
  hideLogInLink,
  onBack,
}: PhoneNumberScreenUIProps) {
  const [countryCode, setCountryCode] = useState("+1")
  const [phoneNumber, setPhoneNumber] = useState("")

  function renderLogInButton() {
    if (hideLogInLink || !switchToLogInScreen) {
      return null
    }
    if (Platform.OS === "web" && !showLogInButtonOnWeb) {
      return null
    }

    return (
      <LabelWrapper>
        <Text
          fontSize={18}
          lineHeight={28}
          color="gray800"
          fontFamily="IBMPlexSans-Regular"
          onPress={switchToLogInScreen}
        >
          Already have an account?{" "}
          <Text fontSize={18} lineHeight={28} fontFamily="IBMPlexSans-SemiBold" color="royalBlue">
            Log in
          </Text>
        </Text>
      </LabelWrapper>
    )
  }

  return (
    <KeyboardAvoidingView keyboardVerticalOffset={0} testID="phoneNumberScreen">
      <OnboardingScreenWrapper stepNumber={1} onBack={onBack}>
        <Container>
          <ScrollView
            style={{ flex: 1 }}
            contentContainerStyle={{ flexGrow: 1 }}
            alwaysBounceVertical={false}
            keyboardShouldPersistTaps="always"
          >
            <CloseKeyboardContainer>
              <Content>
                <Spacing top={44} />
                <Text type="h1" color="primary">
                  Let’s get started
                </Text>
                <Spacing top={12} />
                <Text
                  fontSize={20}
                  lineHeight={32}
                  color="gray900"
                  fontFamily="IBMPlexSans-Regular"
                  accessibilityLabel="Enter your phone number"
                >
                  Enter your phone number and we will send you a confirmation code.
                </Text>
                <Spacing top={16} />
                <PhoneNumberInput
                  phoneNumber={phoneNumber}
                  setPhoneNumber={setPhoneNumber}
                  countryCode={countryCode}
                  setCountryCode={setCountryCode}
                />
                <Spacing top={16} />
                {renderLogInButton()}
              </Content>
              <ButtonWrapper>
                <ButtonV2
                  title="Continue"
                  onPress={() => proceed({ countryCode, phoneNumber })}
                  variant="blue2"
                  loading={isSendingText}
                  disabled={!phoneNumber.length}
                  testID="phoneNumberContinueButton"
                />
              </ButtonWrapper>
            </CloseKeyboardContainer>
          </ScrollView>
        </Container>
      </OnboardingScreenWrapper>
    </KeyboardAvoidingView>
  )
}

const Container = styled.View`
  flex: 1;
  max-width: 500px;
  width: 100%;
  align-self: center;
`

const Content = styled.View`
  flex: 1;
  padding: 0 20px;
`

const ButtonWrapper = styled.View`
  margin-left: 20px;
  margin-right: 20px;
  padding-bottom: ${() => (Platform.OS === "web" ? "20px" : 0)};
`

const LabelWrapper = styled.View``
